import type { MtoRun } from '@entities/mto';
import {
  fmtDateTime,
  isMtoRunActive,
  moduleTitle,
  statusLabel,
  statusTone,
} from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Progress } from '@shared/ui/Progress';
import { IconArrowRight } from '@shared/ui/icons';
import { runTitle } from '../helpers';
import {
  EmptyState,
  ListHead,
  ModuleTag,
  Muted,
  OpenHint,
  RunCard,
  RunMeta,
  RunTitle,
  RunTop,
  ScopeBtn,
  ScopeSwitch,
  Stack,
} from '../styles';
import type { Scope } from '../types';

/** Список карточек обработок. `showModule` — выводить бейдж модуля (для сводного
 *  реестра, где смешаны модули). Переключатель «все отдела / мои». */
export function RunsStack({
  runs,
  showModule,
  busy,
  isLoading,
  emptyText,
  scope,
  onScope,
  onOpen,
}: {
  runs: MtoRun[];
  showModule: boolean;
  busy: boolean;
  isLoading: boolean;
  emptyText: string;
  scope: Scope;
  onScope: (s: Scope) => void;
  onOpen: (id: string) => void;
}) {
  return (
    <Stack>
      <ListHead>
        <Muted>
          Обработок: {runs.length}
          {busy ? ' · обновление…' : ''}
        </Muted>
        <ScopeSwitch role="group" aria-label="Чьи обработки показывать">
          <ScopeBtn
            type="button"
            $active={scope === 'all'}
            aria-pressed={scope === 'all'}
            onClick={() => onScope('all')}
          >
            Все отдела
          </ScopeBtn>
          <ScopeBtn
            type="button"
            $active={scope === 'mine'}
            aria-pressed={scope === 'mine'}
            onClick={() => onScope('mine')}
          >
            Мои
          </ScopeBtn>
        </ScopeSwitch>
      </ListHead>

      {runs.map((r) => (
        <RunCard
          key={r.id}
          $accent
          role="button"
          tabIndex={0}
          onClick={() => onOpen(r.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(r.id);
            }
          }}
        >
          <RunTop>
            <RunTitle title={runTitle(r)}>{runTitle(r)}</RunTitle>
            <Badge $tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
          </RunTop>
          {isMtoRunActive(r.status) && <Progress percent={r.progress_percent} />}
          <RunMeta>
            <span>{fmtDateTime(r.created_at)}</span>
            {showModule && r.module && <ModuleTag>{moduleTitle(r.module)}</ModuleTag>}
            <OpenHint>
              Открыть
              <IconArrowRight size={14} />
            </OpenHint>
          </RunMeta>
        </RunCard>
      ))}

      {runs.length === 0 && !isLoading && <EmptyState>{emptyText}</EmptyState>}
    </Stack>
  );
}
